import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Database, Shield, GitBranch, Lock, Search, Users,
  ArrowRight, CheckCircle, Sparkles, TrendingUp, BarChart3
} from 'lucide-react';

const DataMaturitySection = () => {
  const dimensions = [
    { icon: Shield, name: 'Data Governance', description: 'Ownership, policies, and compliance' },
    { icon: Database, name: 'Data Quality', description: 'Accuracy, completeness, and consistency' },
    { icon: GitBranch, name: 'Data Architecture', description: 'Integration, storage, and infrastructure' },
    { icon: BarChart3, name: 'Analytics & BI', description: 'Insights, reporting, and visualization' },
    { icon: Lock, name: 'Data Security', description: 'Protection, privacy, and access control' },
    { icon: Users, name: 'Data Culture', description: 'Literacy, skills, and data-driven mindset' },
  ];

  const features = [
    'Architect-level questions',
    'Industry benchmarks',
    'Strategic roadmap',
    'Gap analysis',
    'Investment priorities',
    'PPTX report download'
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-slate-800 via-indigo-950 to-slate-900 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-indigo-500/5 to-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 rounded-full px-4 py-1.5 mb-6">
            <Database className="w-4 h-4 text-indigo-400" />
            <span className="text-sm font-medium text-indigo-300">Data-Driven Excellence</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Data Maturity Assessment
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Evaluate your organization&apos;s data management capabilities with our comprehensive 
            assessment covering governance, quality, architecture, and analytics.
          </p>
        </div>

        {/* Main content grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Dimensions */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Search className="w-5 h-5 text-indigo-400" />
              6 Assessment Dimensions
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {dimensions.map((dim, index) => {
                const Icon = dim.icon;
                return (
                  <div 
                    key={index}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:bg-white/10 hover:border-indigo-500/30 transition-all group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:from-indigo-500/30 group-hover:to-purple-500/30 transition-colors">
                        <Icon className="w-5 h-5 text-indigo-400" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white text-sm">{dim.name}</h4>
                        <p className="text-xs text-gray-500 mt-0.5">{dim.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right side - Features & CTA */}
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-400" />
              What You&apos;ll Get
            </h3>
            
            <div className="grid grid-cols-2 gap-3 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="text-sm text-gray-300">{feature}</span>
                </div>
              ))}
            </div>

            {/* Maturity levels preview */}
            <div className="bg-black/20 rounded-xl p-4 mb-8">
              <p className="text-xs text-gray-500 mb-3 uppercase tracking-wide">Maturity Levels</p>
              <div className="flex items-center gap-1">
                <div className="flex-1 h-2 bg-red-500/60 rounded-l-full" title="Initial" />
                <div className="flex-1 h-2 bg-orange-500/60" title="Developing" />
                <div className="flex-1 h-2 bg-yellow-500/60" title="Defined" />
                <div className="flex-1 h-2 bg-green-500/60" title="Managed" />
                <div className="flex-1 h-2 bg-indigo-600/80 rounded-r-full" title="Optimizing" />
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-gray-500">
                <span>Initial</span>
                <span>Developing</span>
                <span>Defined</span>
                <span>Managed</span>
                <span>Optimizing</span>
              </div>
            </div>

            {/* CTA */}
            <Link 
              to="/data-maturity"
              className="group flex items-center justify-center gap-2 w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold py-4 px-6 rounded-xl transition-all shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/30"
              data-testid="launch-data-maturity"
            >
              <Database className="w-5 h-5" />
              Start Data Assessment
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            
            <p className="text-center text-xs text-gray-500 mt-3">
              Takes ~10-15 minutes • AI-powered analysis • PowerPoint export
            </p>
          </div>
        </div>

        {/* Bottom stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: '6', label: 'Assessment Dimensions' },
            { value: '24', label: 'Technical Questions' },
            { value: '5', label: 'Maturity Levels' },
            { value: '20+', label: 'Industries Covered' }
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                {stat.value}
              </div>
              <div className="text-sm text-gray-500 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DataMaturitySection;
